/** Wire form of the input record (05 §1): i8 move axes, u16 aim bearing,
 *  u8 button bits. The sim consumes the decoded `PlayerInput`. */
import type { PlayerInput } from '../sim/types.js'

export interface InputWire {
  seq: number
  tick: number
  /** i8 −127..127 */
  mx: number
  my: number
  /** u16 0..65535 ↔ 0..2π */
  aim: number
  /** bit0 THROW, bit1 SHIELD, bit2 DASH, bit3 JUMP, bit4 USE (reserved) */
  b: number
  /** ms, diagnostics */
  t: number
}

export const BTN_THROW = 1
export const BTN_SHIELD = 2
export const BTN_DASH = 4
export const BTN_JUMP = 8 // spec §1.2 (Sprint 10)
export const BTN_USE = 16 // reserved

const TWO_PI = Math.PI * 2
const clampI8 = (v: number): number => Math.max(-127, Math.min(127, Math.round(v * 127)))

/** The move axis exactly as the server will decode it. The predicting client must step with this value,
 *  not the raw float, or every fractional move vector (a yaw-rotated one in the open world) mispredicts. */
export const quantizeAxis = (v: number): number => clampI8(v) / 127

export function encodeInput(i: PlayerInput): InputWire {
  let a = i.aim % TWO_PI
  if (a < 0) a += TWO_PI
  return {
    seq: i.seq, tick: i.tick,
    mx: clampI8(i.mx), my: clampI8(i.my),
    aim: Math.round((a / TWO_PI) * 65535) & 0xffff,
    b: (i.throw ? BTN_THROW : 0) | (i.shield ? BTN_SHIELD : 0) | (i.dash ? BTN_DASH : 0) | (i.jump ? BTN_JUMP : 0),
    t: i.t,
  }
}

export function decodeInput(w: InputWire): PlayerInput {
  return {
    seq: w.seq, tick: w.tick,
    mx: w.mx / 127, my: w.my / 127,
    aim: (w.aim / 65535) * TWO_PI,
    throw: (w.b & BTN_THROW) !== 0, shield: (w.b & BTN_SHIELD) !== 0, dash: (w.b & BTN_DASH) !== 0, jump: (w.b & BTN_JUMP) !== 0,
    t: w.t,
  }
}
